import locale from "csi/locale";
import csi from "csi/main";
import { C8Node, cnl, ICache } from "csi/typings";
import { resolveNode, resolveNodes } from "./nodeService";

export interface IColorIntent {
	id: cnl;
};

export interface IDataSheetIntent {
	id: string;
};

export interface ILabelIntent {
	id: string;
};

export interface IDesignIntent {
	colorIntents?: IColorIntent[];
	dataSheetIntents?: IDataSheetIntent[];
	labelIntents?: ILabelIntent[];
	printDesignColorIntents?: IColorIntent[];
	printSpecColorSpecIntents?: IColorIntent[];
	useAsDefaultImage?: boolean;
};

export interface IContainerIntent {
	containerCnl: cnl;
	designIntents: IDesignIntent;
};

export interface IImageIntent {
	containerIntents?: IContainerIntent[];
	designIntents: IDesignIntent;
}

function getDocumentRevisionImageIntent(docRev: C8Node, imageKey: string): IImageIntent {
	const intentJson = docRev?.ImageConfiguration[imageKey];
	return intentJson ? JSON.parse(intentJson) : null;
}

function getColorIntents(designIntent: IDesignIntent): IColorIntent[] {
	const {
		colorIntents,
		printDesignColorIntents,
		printSpecColorSpecIntents,
	} = designIntent;

	// find the first 'ColorIntent'-type array that actually has a value;
	//  all of them are usually present in the DesignIntents data structure.
	if (Array.isArray(colorIntents) && colorIntents.length) {
		return colorIntents;
	} else if (Array.isArray(printDesignColorIntents) && printDesignColorIntents.length) {
		return printDesignColorIntents;
	} else if (Array.isArray(printSpecColorSpecIntents) && printSpecColorSpecIntents.length) {
		return printSpecColorSpecIntents;
	} else {
		return [];
	}
}

/**
 * Return names of design intent colors
 */
export function designIntentGetColors(designIntent: IDesignIntent, cache: ICache): string[] {
	const colorIntents = getColorIntents(designIntent);
	const colorNodes = csi.transform(colorIntents, ({ id }) => cache.get(id));
	return colorNodes?.map((node) => locale.getNodeConfiguredName(node, cache));
}

/**
 * Return type names of design intent datasheets
 */
export function designIntentGetDataSheets(designIntent: IDesignIntent): string[] {
	return designIntent.dataSheetIntents?.map(({ id }) => locale.type(id));
}

/**
 * Return names of design intent image labels
 */
export function designIntentGetLabels(designIntent: IDesignIntent): string[] {
	return designIntent.labelIntents?.map(({ id }) => locale.getEnumName(id));
}

/**
 * Determine if image was generated by image intents
 */
export function imageHasIntent(image: C8Node): boolean {
	return !!image.ImageIntent;
}

/**
 * Extract image intent from image node, assuming all nodes are resolved
 */
export function imageGetDesignIntent(image: C8Node, contextUrl: cnl, cache: ICache): IDesignIntent {
	const { GeneratedFor, ImageIntent } = image;
	const docRev = cache.get(GeneratedFor);
	const imageIntent = getDocumentRevisionImageIntent(docRev, ImageIntent);
	return imageIntent ? imageIntentGetDesignIntent(imageIntent, contextUrl) : null;
}

/**
 * Extract image intent from image node
 */
export async function imageGetImageIntent(image: C8Node, cache: ICache): Promise<IImageIntent> {
	const { GeneratedFor, ImageIntent } = image;
	const docRev = await resolveNode(GeneratedFor, cache);
	return getDocumentRevisionImageIntent(docRev, ImageIntent);
}

/**
 * Return design intents for given context
 */
export function imageIntentGetDesignIntent(imageIntent: IImageIntent, contextUrl: cnl): IDesignIntent {
	const { containerIntents, designIntents } = imageIntent;
	const containerIntent = containerIntents?.find(({ containerCnl }) => containerCnl === contextUrl);
	return containerIntent?.designIntents || designIntents;
}

/**
 * Resolve nodes necessary to render image intents
 */
export async function imageResolveImageIntent(image: C8Node, contextUrl: cnl, cache: ICache): Promise<void> {
	const imageIntent = await imageGetImageIntent(image, cache);
	if (imageIntent) {
		const designIntent = imageIntentGetDesignIntent(imageIntent, contextUrl);
		if (designIntent) {
			const colorIntents = getColorIntents(designIntent);
			if (colorIntents) {
				const urls = colorIntents.map(({ id }) => id);
				await resolveNodes(urls, cache);
			}
		}
	}
}